class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    this.boundSetHeight = this.setHeight.bind(this);
    if (window.ResizeObserver) {
      new ResizeObserver(this.boundSetHeight).observe(this);
    }
    window.addEventListener('scroll', this.boundSetHeight, { passive: true });

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundSetHeight);
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);
      const rect = this.getBoundingClientRect();
      const flyoutTop = Math.round(rect.bottom + 32);

      if (window.matchMedia('(min-width: 1024px)').matches) {
        const brandClusterWidth = Math.round(
          this.querySelector('.header__brand-cluster')?.getBoundingClientRect().width ?? 0
        );
        if (brandClusterWidth) {
          document.documentElement.style.setProperty('--header-brand-cluster-width', `${brandClusterWidth}px`);
        }
      } else {
        document.documentElement.style.removeProperty('--header-brand-cluster-width');
      }

      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      document.documentElement.style.setProperty('--header-flyout-top', `${flyoutTop}px`);
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: 'header-pinned',
    };
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  getDefaultScrolledThreshold() {
    return this.headerBounds.top + this.firstScrollTop;
  }

  getHomepageScrolledThreshold() {
    if (this.homepageScrolledThreshold !== undefined) {
      return this.homepageScrolledThreshold;
    }

    if (!this.enableTransparent) {
      this.homepageScrolledThreshold = this.getDefaultScrolledThreshold();
      return this.homepageScrolledThreshold;
    }

    const firstContentSection = document.querySelector('#MainContent > .shopify-section:first-child');
    if (!firstContentSection) {
      this.homepageScrolledThreshold = this.getDefaultScrolledThreshold();
      return this.homepageScrolledThreshold;
    }

    this.homepageScrolledThreshold = Math.round(firstContentSection.getBoundingClientRect().bottom + window.scrollY);
    return this.homepageScrolledThreshold;
  }

  shouldUseScrolledState(scrollTop) {
    const threshold = this.enableTransparent ? this.getHomepageScrolledThreshold() : this.getDefaultScrolledThreshold();
    const thresholdBuffer = this.enableTransparent ? 24 : 0;

    if (this.headerSection.classList.contains('header-scrolled')) {
      return scrollTop > threshold - thresholdBuffer;
    }

    return scrollTop > threshold + thresholdBuffer;
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    // Cache initial bounds for better performance
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.onScrollHandler.bind(this), false);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (this.shouldUseScrolledState(scrollTop)) {
        headerSection.classList.add('header-scrolled');

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.classes.pinned);
        } else {
          // Sticky on scroll up.
          if (scrollTop < this.currentScrollTop || scrollTop < headerBoundsBottom + 100) {
            document.body.classList.add(this.classes.pinned);
          } else {
            document.body.classList.remove(this.classes.pinned);
          }
        }
      } else {
        headerSection.classList.remove('header-scrolled');
        document.body.classList.remove(this.classes.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

let headerTopMenuTransitionInFlight = 0;


// Update the menu backdrop to keep it snappy
function updateHeaderMenuBackdrop() {
  const nav = document.querySelector('.header__menu');
  const menus = nav ? [...nav.querySelectorAll('details[level="top"]')] : [];
  const keepBackdrop =
    headerTopMenuTransitionInFlight > 0 ||
    menus.some((menu) => menu._open || menu.hasAttribute('open'));

  document.body.classList.toggle('has-dropdown-menu', keepBackdrop);
}

// Close stray top level menus when the menu is closed
function closeStrayTopLevelMenus() {
  const nav = document.querySelector('.header__menu');
  if (!nav) {
    return;
  }

  for (const menu of nav.querySelectorAll('details[level="top"]')) {
    if (menu._open) {
      continue;
    }

    menu.summaryElement?.removeAttribute('open');
    menu.contentElement?.removeAttribute('open');
    if (!menu.hasAttribute('open')) {
      continue;
    }

    menu.removeAttribute('open');
    FoxTheme.Motion.animate(
      menu.contentElement,
      { opacity: 0, visibility: 'hidden' },
      { duration: 0 }
    );
  }
}

let headerTopMenuTransitionQueue = Promise.resolve();
const pendingTopMenuTransitions = new WeakMap();

function enqueueHeaderTopMenuTransition(menu) {
  const pending = pendingTopMenuTransitions.get(menu);
  if (pending) {
    return pending;
  }

  headerTopMenuTransitionInFlight += 1;
  updateHeaderMenuBackdrop();

  const promise = headerTopMenuTransitionQueue
    .then(async () => {
      pendingTopMenuTransitions.delete(menu);

      if (
        menu._open &&
        menu.trigger === 'hover' &&
        !FoxTheme.config.isTouch &&
        !menu.matches(':hover')
      ) {
        menu._open = false;
      }

      await menu._runTransition(menu._open);
    })
    .catch(() => { })
    .finally(() => {
      headerTopMenuTransitionInFlight = Math.max(0, headerTopMenuTransitionInFlight - 1);
      updateHeaderMenuBackdrop();
      closeStrayTopLevelMenus();
    });

  pendingTopMenuTransitions.set(menu, promise);
  headerTopMenuTransitionQueue = promise;
  return promise;
}

class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute('open');

    // Event listeners for summary element
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Setup hover detection with debouncing
    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener);
    this.addEventListener('mouseleave', this.detectHoverListener);
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      if (this.isConnected) {
        if (this.level === 'top') {
          updateHeaderMenuBackdrop();
        }
        this._transitionPromise = this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    // For touch devices, always use click events
    if (FoxTheme.config.isTouch) {
      return 'click';
    }

    // For non-touch devices, check for custom trigger attribute
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  getOtherOpenTopLevelMenus() {
    const nav = this.closest('.header__menu');
    if (!nav) {
      return [];
    }
    // Get all top level menus that are open and not the current menu
    return [...nav.querySelectorAll('details[level="top"]')].filter(
      (menu) => menu !== this && (menu._open || menu.hasAttribute('open'))
    );
  }

  async closeNestedMenus(menu) {
    const nestedMenus = [...menu.querySelectorAll('details[is="details-dropdown"]')].filter(
      (details) => details !== menu && details._open
    );
    for (const nestedMenu of nestedMenus) {
      nestedMenu.open = false;
      if (nestedMenu._transitionPromise) {
        await nestedMenu._transitionPromise;
      }
    }
  }

  async closeOtherTopLevelMenus() {
    for (const menu of this.getOtherOpenTopLevelMenus()) {
      await this.closeNestedMenus(menu);
      menu._open = false;
      await menu._runTransition(false);
    }
  }

  transition(value) {
    if (this.level === 'top') {
      return enqueueHeaderTopMenuTransition(this);
    }
    return this._runTransition(value);
  }

  async _runTransition(value) {
    if (value) {
      if (!this._open) return;

      if (this.level === 'top') {
        await this.closeOtherTopLevelMenus();
      }

      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => this.contentElement.setAttribute('open', ''), 100);

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      // Perform the transition in animation
      await this.showWithTransition();

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();
    } else {
      if (this._open) {
        return;
      }

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }

      if (this.level === 'top') {
        updateHeaderMenuBackdrop();
      }
    }
  }

  async showWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: ['hidden', 'visible'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [1, 0], visibility: ['visible', 'hidden'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.25,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      if (event.type === 'mouseenter') {
        clearTimeout(this.hoverTimer);
        this.open = true;
        return;
      }

      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => {
        if (!this.matches(':hover')) {
          this.open = false;
        }
      }, 220);
    }
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  static SUBMENU_TRANSITION_MS = 150;

  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick() {
    requestAnimationFrame(() => {
      if (this.open) {
        this.openSubmenu();
      } else {
        this.closeSubmenu();
      }
    });
  }

  onCloseButtonClick(event) {
    event?.preventDefault();
    this.closeSubmenu();
  }

  openSubmenu() {
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = null;
    }

    this.classList.remove('closing');
    this.parent?.classList.add('active');
    this.classList.add('active');
    this.summary?.setAttribute('aria-expanded', 'true');
  }

  closeSubmenu() {
    if (this.classList.contains('closing')) {
      return;
    }

    this.querySelectorAll('details[is=menu-drawer-details]').forEach((details) => {
      if (details === this) {
        return;
      }

      if (details._closeTimeout) {
        clearTimeout(details._closeTimeout);
        details._closeTimeout = null;
      }

      details.classList.remove('active', 'closing');
      details.parent?.classList.remove('active');
      details.summary?.setAttribute('aria-expanded', 'false');
      details.removeAttribute('open');
    });

    const duration = FoxTheme.config.motionReduced ? 0 : MenuDrawerDetails.SUBMENU_TRANSITION_MS;

    const finishClose = () => {
      this._closeTimeout = null;
      this.classList.remove('closing', 'active');
      this.parent?.classList.remove('active');
      this.removeAttribute('open');
    };

    this.classList.add('closing');
    this.summary?.setAttribute('aria-expanded', 'false');

    if (duration === 0) {
      finishClose();
      return;
    }

    this._closeTimeout = window.setTimeout(finishClose, duration);
  }

  closeAnimation() {
    this.closeSubmenu();
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
    this._activeTabIndex = 0;
    this._onTabClick = this._onTabClick.bind(this);
  }

  get headerTabs() {
    return document.getElementById('HeaderMenuTabs');
  }

  get tabButtons() {
    return this.headerTabs?.querySelectorAll('[data-menu-drawer-tab]') ?? [];
  }

  get panels() {
    return this.querySelectorAll('[data-menu-drawer-panel]');
  }

  get menuItems() {
    const activePanel = this.panels[this._activeTabIndex];
    if (!activePanel) {
      return [];
    }

    return activePanel.querySelectorAll('.menu-drawer__menus:not(.active) > li');
  }

  connectedCallback() {
    super.connectedCallback();
    this.tabButtons.forEach((button) => {
      button.addEventListener('click', this._onTabClick);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.tabButtons.forEach((button) => {
      button.removeEventListener('click', this._onTabClick);
    });
  }

  _onTabClick(event) {
    const directUrl = event.currentTarget.dataset.menuDirectUrl;
    if (directUrl) {
      this.tabButtons.forEach((button) => {
        button.setAttribute('aria-selected', button === event.currentTarget ? 'true' : 'false');
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => window.location.assign(directUrl));
      });
      return;
    }

    const index = Number.parseInt(event.currentTarget.dataset.tabIndex, 10);
    if (!Number.isNaN(index)) {
      this.activateTab(index);
    }
  }

  showTab(index) {
    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.toggleAttribute('hidden', !isActive);
      panel.classList.toggle('hidden', !isActive);
    });

    this.tabButtons.forEach((button, buttonIndex) => {
      button.setAttribute('aria-selected', buttonIndex === index ? 'true' : 'false');
    });

    this._activeTabIndex = index;
    requestAnimationFrame(() => this.refreshProductLists());
  }

  refreshProductLists() {
    const activePanel = this.panels[this._activeTabIndex];
    if (!activePanel) return;

    activePanel.querySelectorAll('menu-product-list').forEach((list) => {
      list.refreshSlide?.();
    });
  }

  activateTab(index) {
    if (index === this._activeTabIndex) return;

    this.resetSubmenus();
    this.showTab(index);
    this.animateMenuItems();
  }

  resetSubmenus() {
    this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
      if (menu.hasAttribute('open')) {
        menu.closeSubmenu();
      }
    });
  }

  syncDrawerTop() {
    const tabs = this.headerTabs;
    if (!tabs || !document.body.classList.contains('menu-drawer-open')) return;

    const gap = Number.parseFloat(
      getComputedStyle(tabs).getPropertyValue('--header-menu-tabs-gap')
    );
    const offset = Math.round(tabs.getBoundingClientRect().bottom + (Number.isNaN(gap) ? 16 : gap));

    document.documentElement.style.setProperty('--menu-drawer-top', `${offset}px`);
  }

  prepareToShow() {
    super.prepareToShow();
    document.body.classList.add('menu-drawer-open');
    this.headerTabs?.setAttribute('aria-hidden', 'false');
    this.showTab(0);

    this._boundSyncDrawerTop = this.syncDrawerTop.bind(this);
    requestAnimationFrame(() => {
      this.syncDrawerTop();
      requestAnimationFrame(() => {
        this.syncDrawerTop();
        this.refreshProductLists();
      });
    });
    window.addEventListener('resize', this._boundSyncDrawerTop, { passive: true });
    window.addEventListener('scroll', this._boundSyncDrawerTop, { passive: true });

    setTimeout(() => {
      this.animateMenuItems();
    }, 300);
  }

  animateMenuItems() {
    const items = this.menuItems;
    if (!items.length) return;

    FoxTheme.Motion.animate(
      items,
      {
        transform: ['translateX(-20px)', 'translateX(0)'],
        opacity: [0, 1],
      },
      {
        duration: 0.6,
        easing: [0.075, 0.82, 0.165, 1],
        delay: FoxTheme.Motion.stagger(0.1),
      }
    ).finished.then(() => {
      items.forEach((item) => item.removeAttribute('style'));
    });
  }

  handleAfterHide() {
    super.handleAfterHide();
    this.headerTabs?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-drawer-open');
    if (this._boundSyncDrawerTop) {
      window.removeEventListener('resize', this._boundSyncDrawerTop);
      window.removeEventListener('scroll', this._boundSyncDrawerTop);
    }
    document.documentElement.style.removeProperty('--menu-drawer-top');
    this.showTab(0);
    setTimeout(() => {
      this.resetSubmenus();
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    if (!this.container) return;

    this.carousel = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label',
    });
    this.carousel.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', () => {
        const slide = element.closest('.swiper-slide');

        slide &&
          this.carousel.slider.slideTo(this.carousel.slider.slides.indexOf(slide));
      });
    });
  }

  refreshSlide() {
    if (!this.carousel?.slider) return;

    this.carousel.slider.update();
    this.carousel.slider.slideTo(0, 0);
  }
}
customElements.define('menu-product-list', MenuProductList);
